/*
  FILE: assets/eg-card-behaviors.js
  Shared product-card behaviour, loaded once per page (defer) instead of repeating inline
  <script> blocks in every card:
  - wishlist heart visual state sync (was inline in snippets/eg-wishlist-card-button.liquid)
  - overlay colour swatch navigation (was inline in blocks/eg-ov-swatches.liquid)
  - colour swatch hover preview: the colour's main photo over the card photo (mouse only), and
    that colour's number, name, line and price in place of the card's (data-eg-ov-swatch-info)
*/

(function () {
  if (window.__egWishlistVisualSyncInit) return;
  window.__egWishlistVisualSyncInit = true;

  var BUTTON_SELECTOR = '.eg-wishlist-card-button__btn[data-product-handle][data-variant-id]';

  function isPressed(button) {
    if (!button) return false;

    if (button.getAttribute('aria-pressed') === 'true') return true;
    if (button.getAttribute('data-active') === 'true') return true;
    if (button.classList && button.classList.contains('is-active')) return true;

    return false;
  }

  function syncButton(button) {
    if (!button) return;
    button.setAttribute('data-eg-visual-pressed', isPressed(button) ? 'true' : 'false');
  }

  function hydrateAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(BUTTON_SELECTOR).forEach(syncButton);
  }

  hydrateAll(document);

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      var target = mutation.target;

      if (target && target.nodeType === 1 && target.matches && target.matches(BUTTON_SELECTOR)) {
        syncButton(target);
      }

      mutation.addedNodes.forEach(function (node) {
        if (!node || node.nodeType !== 1) return;

        if (node.matches && node.matches(BUTTON_SELECTOR)) {
          syncButton(node);
        }

        if (node.querySelectorAll) {
          node.querySelectorAll(BUTTON_SELECTOR).forEach(syncButton);
        }
      });
    });
  });

  // Only the attributes isPressed() reads on the button. ('class' used to be here too: every class
  // change on the page was delivered to this observer, and the heart's is-active class sits on the
  // wrapper, not on the button.)
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-pressed', 'data-active']
  });

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target && event.target.closest
        ? event.target.closest(BUTTON_SELECTOR)
        : null;

      if (!button) return;

      syncButton(button);
    },
    true
  );
})();

(function () {
  if (window.__egOvSwatchNavInit) return;
  window.__egOvSwatchNavInit = true;

  var SWATCH_SELECTOR = '.eg-ov-swatches [data-eg-ov-swatch-url]';

  function swatchFromEvent(event) {
    var target = event.target;
    return target && target.closest ? target.closest(SWATCH_SELECTOR) : null;
  }

  function go(event, button) {
    event.preventDefault();
    event.stopPropagation();

    var url = button.getAttribute('data-eg-ov-swatch-url');
    if (url) window.location.href = url;
  }

  /*
    Swatch buttons can sit inside the card <a>, so the click must not reach the link.
    Capture on document stops it before any element below sees it. Carousels register their own
    document-capture handlers earlier (inline) and call preventDefault after a drag; this file is
    deferred, so their handler runs first and a dragged click is ignored here.
  */
  document.addEventListener(
    'click',
    function (event) {
      var button = swatchFromEvent(event);
      if (!button || event.defaultPrevented) return;
      go(event, button);
    },
    true
  );

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      var button = swatchFromEvent(event);
      if (!button) return;
      go(event, button);
    },
    true
  );
})();

(function () {
  if (window.__egOvSwatchPreviewInit) return;
  window.__egOvSwatchPreviewInit = true;

  /*
    Hovering a colour swatch shows that colour's main photo over the card photo
    (data-eg-ov-swatch-image from blocks/eg-ov-swatches.liquid). The swatch list is one hover zone:
    in it the card's second photo stays hidden (section-editorial-grid.css). The last colour
    hovered stays up while the pointer crosses the gaps between swatches; the card photo comes
    back over the current colour's swatch (disabled, so found by position) or when the pointer
    leaves the zone.
  */
  var hoverQuery = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  if (!hoverQuery) return;

  var ZONE_SELECTOR = '.eg-ov-swatches__list';
  var SWATCH_SELECTOR = '.eg-ov-swatches__swatch[data-eg-ov-swatch-image]';
  var PREVIEW_CLASS = 'eg-media__swatch-preview';
  var CURRENT_SELECTOR = '.eg-ov-swatches__swatch[aria-current="true"]';

  var previews = new WeakMap();
  var activeCard = null;
  var activeSwatch = null;

  function closestElement(node, selector) {
    return node && node.nodeType === 1 && node.closest ? node.closest(selector) : null;
  }

  function withWidth(url, width) {
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // Same widths and sizes as the card photo, so the browser picks the same file size for both.
  function srcsetWidths(img) {
    var srcset = img.getAttribute('srcset') || '';
    var widths = [];
    srcset.split(',').forEach(function (candidate) {
      var match = candidate.trim().match(/\s(\d+)w$/);
      if (match) widths.push(Number(match[1]));
    });
    return widths;
  }

  function createPreview(swatch, primary) {
    var src = swatch.getAttribute('data-eg-ov-swatch-image');
    if (!src) return null;

    var img = document.createElement('img');
    img.className = PREVIEW_CLASS;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.decoding = 'async';

    if (primary.tagName === 'IMG') {
      var style = window.getComputedStyle(primary);
      img.style.objectFit = style.objectFit;
      img.style.objectPosition = style.objectPosition;

      var widths = srcsetWidths(primary);
      if (widths.length) {
        img.sizes = primary.getAttribute('sizes') || '100vw';
        img.srcset = widths.map(function (width) {
          return withWidth(src, width) + ' ' + width + 'w';
        }).join(', ');
      }
    }
    img.src = src;

    // Above the card photo and its hover photo, below the overlay and the wishlist heart.
    var hoverPicture = primary.parentNode.querySelector(':scope > .eg-media__hover-picture');
    var anchor = hoverPicture || primary;
    anchor.parentNode.insertBefore(img, anchor.nextSibling);

    img.addEventListener('load', function () {
      if (activeSwatch === swatch) img.classList.add('is-visible');
    });

    previews.set(swatch, img);
    return img;
  }

  /*
    The hovered colour's words: every text in the card's overlay that reads exactly as the current
    colour's number, name, line or price is set to the hovered colour's, and put back afterwards.
  */
  var swappedTexts = new Map();

  function swatchInfo(swatch) {
    try {
      return JSON.parse(swatch.getAttribute('data-eg-ov-swatch-info') || 'null');
    } catch (error) {
      return null;
    }
  }

  function restoreTexts() {
    swappedTexts.forEach(function (text, element) {
      element.textContent = text;
    });
    swappedTexts.clear();
  }

  function swapTexts(card, swatch) {
    restoreTexts();
    var zone = swatch.closest(ZONE_SELECTOR);
    var current = zone ? zone.querySelector(CURRENT_SELECTOR) : null;
    var from = current ? swatchInfo(current) : null;
    var to = swatchInfo(swatch);
    var overlay = card.querySelector('.eg-pc-overlay');
    if (!from || !to || !overlay) return;
    var leaves = Array.prototype.filter.call(overlay.querySelectorAll('*'), function (element) {
      return !element.children.length && !element.closest('.eg-ov-swatches');
    });
    ['number', 'title', 'subtitle', 'price'].forEach(function (key) {
      var before = from[key] == null ? '' : String(from[key]).trim();
      var after = to[key] == null ? '' : String(to[key]).trim();
      if (!before || !after || before === after) return;
      leaves.forEach(function (element) {
        if (element.textContent.trim() !== before) return;
        if (!swappedTexts.has(element)) swappedTexts.set(element, element.textContent);
        element.textContent = after;
      });
    });
  }

  function hidePreviews(card) {
    restoreTexts();
    if (!card) return;
    card.querySelectorAll('.' + PREVIEW_CLASS + '.is-visible').forEach(function (img) {
      img.classList.remove('is-visible');
    });
  }

  function clearNow() {
    hidePreviews(activeCard);
    activeCard = null;
    activeSwatch = null;
  }

  // Back to the card photo, still inside the zone.
  function showCardPhoto() {
    if (!activeSwatch) return;
    hidePreviews(activeCard);
    activeSwatch = null;
  }

  function overCurrentSwatch(zone, x, y) {
    var current = zone.querySelector(CURRENT_SELECTOR);
    if (!current) return false;
    var r = current.getBoundingClientRect();
    return x >= r.left - 1 && x <= r.right + 1 && y >= r.top - 1 && y <= r.bottom + 1;
  }

  function show(card, swatch) {
    if (activeSwatch === swatch) return;

    var primary = card.querySelector('.eg-media__img--primary');
    if (!primary || !primary.parentNode) return;

    // The first hover in a card starts loading every colour of that card.
    if (!previews.has(swatch)) {
      swatch.closest(ZONE_SELECTOR).querySelectorAll(SWATCH_SELECTOR).forEach(function (other) {
        if (!previews.has(other)) createPreview(other, primary);
      });
    }

    var img = previews.get(swatch);
    if (!img || !img.isConnected) return;

    activeSwatch = swatch;
    swapTexts(card, swatch);
    card.querySelectorAll('.' + PREVIEW_CLASS + '.is-visible').forEach(function (other) {
      if (other !== img) other.classList.remove('is-visible');
    });
    if (img.complete && img.naturalWidth) img.classList.add('is-visible');
  }

  document.addEventListener(
    'pointerover',
    function (event) {
      if (event.pointerType === 'touch' || !hoverQuery.matches) return;

      var zone = closestElement(event.target, ZONE_SELECTOR);
      var card = zone ? zone.closest('.eg-product') : null;

      if (!card) {
        if (activeCard) clearNow();
        return;
      }

      if (activeCard && activeCard !== card) clearNow();
      activeCard = card;

      var swatch = event.buttons ? null : closestElement(event.target, SWATCH_SELECTOR);
      if (swatch) show(card, swatch);
      else if (overCurrentSwatch(zone, event.clientX, event.clientY)) showCardPhoto();
    },
    { passive: true }
  );

  // Inside the zone the list itself is under the pointer in the gaps and over the current
  // colour (its swatch takes no pointer events), so only position tells them apart.
  document.addEventListener(
    'pointermove',
    function (event) {
      if (!activeSwatch || event.pointerType === 'touch') return;
      var zone = event.target;
      if (!zone || !zone.matches || !zone.matches(ZONE_SELECTOR)) return;
      if (overCurrentSwatch(zone, event.clientX, event.clientY)) showCardPhoto();
    },
    { passive: true }
  );

  // The pointer left the window.
  document.addEventListener(
    'pointerout',
    function (event) {
      if (!event.relatedTarget && activeCard) clearNow();
    },
    { passive: true }
  );
})();
